import { hasSelectedEntity } from '../../../../shared/utils/entitySelection';
import { toast } from '../../../../shared/hooks/use-toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import { listStaffResult } from '@/modules/staff/services';
import {
  cancelInProgressDeal as cancelInProgressDealMutation,
  completeInProgressDeal,
  createInProgressDeal,
  getDealsInProgressStaticConfig,
  listInProgressDealsResult,
  saveInProgressDeal,
  returnInProgressDealToProposal,
  subscribeToInProgressPromoted,
  updateInProgressDealStage,
} from '@/modules/deals/services';
import { calculateInProgressStats, filterInProgressByOverview } from '../../utils/inProgress/inProgress.helpers';
import { normalizeSearchValue } from '../../utils/proposals/proposals.helpers';

const {
  dealsInProgress: initialDeals,
  dealStages,
} = getDealsInProgressStaticConfig();

function normalizeClientId(clientId) {
  if (clientId === null || clientId === undefined || clientId === '') {
    return '';
  }

  return String(clientId);
}

function getClientDisplayName(client) {
  return client?.company || client?.businessName || client?.name || '';
}

function buildClientsMap(clients) {
  return new Map(
    (clients || [])
      .map((client) => [normalizeClientId(client?.id), client])
      .filter(([clientId]) => clientId)
  );
}

function resolveClientReference({ clientId, legacyClientName, clientsMap }) {
  const normalizedClientId = normalizeClientId(clientId);
  const legacyName = String(legacyClientName || '').trim();

  if (!normalizedClientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
      clientLegacyName: legacyName,
    };
  }

  const client = clientsMap.get(normalizedClientId);
  if (client) {
    return {
      clientResolvedName:
        getClientDisplayName(client) || legacyName || `Cliente ${normalizedClientId}`,
      clientResolutionStatus: 'resolved',
      clientLegacyName: legacyName,
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientResolutionStatus: 'unresolved_client_reference',
    clientLegacyName: legacyName,
  };
}

function enrichDealsWithClientResolution(deals, clients) {
  const clientsMap = buildClientsMap(clients);

  return (deals || []).map((deal) => {
    const resolution = resolveClientReference({
      clientId: deal.clientId,
      legacyClientName: deal.client,
      clientsMap,
    });

    return {
      ...deal,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

function enrichDealWithClientResolution(deal, clients) {
  return enrichDealsWithClientResolution([deal], clients)[0];
}

export function useInProgressDealsData() {
  const { logout } = useSession();
  const [searchParams] = useSearchParams();
  const clientFilterParam = normalizeClientId(searchParams.get('clientId'));

  const [deals, setDeals] = useState(initialDeals);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [isLoadingDeals, setIsLoadingDeals] = useState(true);
  const [dealsModuleDisabled, setDealsModuleDisabled] = useState(false);
  const [dealsUnavailable, setDealsUnavailable] = useState(false);
  const [dealsError, setDealsError] = useState('');
  const [dealsAuthRequired, setDealsAuthRequired] = useState(false);
  const [dealsTenantDatabaseBlocked, setDealsTenantDatabaseBlocked] = useState(false);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    searchTerm: '',
    urgencyFilter: 'all',
    assigneeFilter: 'all',
    stageFilter: 'all',
    clientFilter: clientFilterParam || 'all',
  });
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);
  const [completionStates, setCompletionStates] = useState({});
  const completionLocksRef = useRef(new Set());
  const completionPromisesRef = useRef(new Map());
  const returnLocksRef = useRef(new Set());
  const selectedDealRef = useRef(null);
  useEffect(() => {
    setFilters((prev) => {
      const nextClientFilter = clientFilterParam || prev.clientFilter || 'all';
      if (prev.clientFilter === nextClientFilter) return prev;
      return { ...prev, clientFilter: nextClientFilter };
    });
  }, [clientFilterParam]);

  const effectiveClientFilter = clientFilterParam || filters.clientFilter || 'all';
  const clientScopedDeals = useMemo(() => {
    return effectiveClientFilter !== 'all'
      ? deals.filter((deal) => hasSelectedEntity(deal, 'client', effectiveClientFilter))
      : deals;
  }, [deals, effectiveClientFilter]);

  const visibleDealsBeforeOverview = useMemo(() => {
    const search = normalizeSearchValue(filters.searchTerm);

    return clientScopedDeals.filter((deal) => {
      const matchesSearch =
        !search ||
        normalizeSearchValue(deal.name).includes(search) ||
        normalizeSearchValue(deal.client).includes(search) ||
        normalizeSearchValue(deal.assignee).includes(search) ||
        normalizeSearchValue(deal.value).includes(search);
      const matchesUrgency =
        filters.urgencyFilter === 'all' || deal.urgency === filters.urgencyFilter;
      const matchesAssignee =
        filters.assigneeFilter === 'all' || (deal.assignee || '').split('; ').includes(filters.assigneeFilter);
      const matchesStage =
        filters.stageFilter === 'all' || deal.stage === filters.stageFilter;

      return matchesSearch && matchesUrgency && matchesAssignee && matchesStage;
    });
  }, [clientScopedDeals, filters]);
  const visibleDeals = useMemo(
    () => filterInProgressByOverview(visibleDealsBeforeOverview, activeOverviewFilter),
    [activeOverviewFilter, visibleDealsBeforeOverview]
  );
  const stats = useMemo(() => calculateInProgressStats(clientScopedDeals, dealStages), [clientScopedDeals, dealStages]);
  const assigneeOptions = useMemo(() => {
    const dealAssignees = deals.flatMap((deal) => (deal.assignee || '').split('; ')).filter(Boolean);
    const staffNames = availableStaff.map((member) => member.name).filter(Boolean);

    return [...new Set([...dealAssignees, ...staffNames])].sort((a, b) =>
      a.localeCompare(b, 'it-IT')
    );
  }, [availableStaff, deals]);
  const hasActiveFilters = useMemo(
    () =>
      filters.searchTerm.trim() !== '' ||
      filters.urgencyFilter !== 'all' ||
      filters.assigneeFilter !== 'all' ||
      filters.stageFilter !== 'all' ||
      (filters.clientFilter && filters.clientFilter !== 'all') ||
      Boolean(activeOverviewFilter),
    [activeOverviewFilter, filters]
  );
  const activeClientFilterLabel = useMemo(() => {
    if (!effectiveClientFilter || effectiveClientFilter === 'all') return '';
    const client = availableClients.find(
      (item) => normalizeClientId(item.id) === normalizeClientId(effectiveClientFilter)
    );

    return getClientDisplayName(client) || `Cliente ${effectiveClientFilter}`;
  }, [availableClients, effectiveClientFilter]);

  useEffect(() => {
    let cancelled = false;

    async function loadDealsContext() {
      const [data, clientsResult, staffResult] = await Promise.all([
        listInProgressDealsResult(),
        listClientsResult(),
        listStaffResult(),
      ]);

      if (!cancelled) {
        const clients = clientsResult.ok ? clientsResult.data : [];
        const moduleDisabled = data.error === 'DEALS_MODULE_DISABLED';
        const tenantDatabaseBlocked =
          data.error === 'DEALS_TENANT_DATABASE_NOT_READY' ||
          data.error === 'DEALS_TENANT_DATABASE_METADATA_MISSING' ||
          data.error === 'DEALS_TENANT_DATABASE_UNAVAILABLE';
        const authRequired =
          data.error === 'IN_PROGRESS_AUTH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_AUTH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_REFRESH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_EXPIRED' ||
          staffResult.error === 'STAFF_AUTH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_REFRESH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_EXPIRED';
        if (authRequired) {
          logout();
        }

        setDeals(data.ok ? enrichDealsWithClientResolution(data.data, clients) : []);
        setAvailableClients(clients);
        setAvailableStaff(staffResult.ok ? staffResult.data : []);
        setIsLoadingDeals(false);
        setDealsModuleDisabled(moduleDisabled);
        setDealsUnavailable(!data.ok && data.error !== 'IN_PROGRESS_AUTH_REQUIRED' && !moduleDisabled && !tenantDatabaseBlocked);
        setDealsAuthRequired(data.error === 'IN_PROGRESS_AUTH_REQUIRED');
        setDealsTenantDatabaseBlocked(tenantDatabaseBlocked);
        setDealsError(data.ok ? '' : data.message || data.error || '');
        setClientsUnavailable(!clientsResult.ok);
        setClientsError(clientsResult.ok ? '' : clientsResult.error || '');
        setStaffUnavailable(!staffResult.ok);
        setStaffError(staffResult.ok ? '' : staffResult.message || staffResult.error || '');
      }
    }

    loadDealsContext();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeToInProgressPromoted((deal) => {
      const resolvedDeal = enrichDealWithClientResolution(deal, availableClients);

      setDeals((prev) => {
        const existing = prev.some((item) => item.id === resolvedDeal.id);

        if (existing) {
          return prev.map((item) => (item.id === resolvedDeal.id ? resolvedDeal : item));
        }

        return [resolvedDeal, ...prev].sort((left, right) =>
          String(right.dataInizio || '').localeCompare(String(left.dataInizio || ''))
        );
      });
    });
  }, [availableClients]);

  function openDealDetails(deal) {
    selectedDealRef.current = deal;
    setSelectedDeal(deal);
    setIsDetailDialogOpen(true);
  }

  function closeDealDetails() {
    selectedDealRef.current = null;
    setIsDetailDialogOpen(false);
    setSelectedDeal(null);
  }

  function openCreateDialog() {
    setIsCreateDialogOpen(true);
  }

  function closeCreateDialog() {
    setIsCreateDialogOpen(false);
  }

  function setOverviewFilter(filterKey) {
    setActiveOverviewFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      setFilters({
        searchTerm: '',
        urgencyFilter: 'all',
        assigneeFilter: 'all',
        stageFilter: 'all',
        clientFilter: clientFilterParam || 'all',
      });
      return next;
    });
  }

  async function handleCreateDeal(deal) {
    const result = await createInProgressDeal(deal);
    if (!result.ok) return result;

    const resolvedDeal = enrichDealWithClientResolution(result.data.deal, availableClients);

    setDeals((prev) =>
      [resolvedDeal, ...prev.filter((item) => item.id !== resolvedDeal.id)].sort((left, right) =>
        String(right.dataInizio || '').localeCompare(String(left.dataInizio || ''))
      )
    );
    setIsCreateDialogOpen(false);
    return result;
  }

  async function handleSaveDeal(updatedDeal) {
    const result = await saveInProgressDeal(updatedDeal);
    if (!result.ok) return result;

    const deal = enrichDealWithClientResolution(result.data.deal, availableClients);

    setDeals((prev) =>
      prev.map((item) => (item.id === deal.id ? deal : item))
    );
    selectedDealRef.current = deal;
    setSelectedDeal(deal);
    return result;
  }

  async function handleCancelDeal(dealId) {
    const result = await cancelInProgressDealMutation(dealId);
    if (!result.ok) return result;

    setDeals((prev) => prev.filter((item) => item.id !== dealId));
    if (selectedDealRef.current?.id === dealId) {
      closeDealDetails();
    }
    return result;
  }

  async function handleReturnToProposal(dealId) {
    const lockKey = String(dealId);
    if (returnLocksRef.current.has(lockKey)) {
      return buildLockedReturnResult();
    }
    returnLocksRef.current.add(lockKey);
    try {
      const result = await returnInProgressDealToProposal(dealId);
      if (!result.ok) return result;
      setDeals((prev) => prev.filter((item) => item.id !== dealId));
      if (selectedDealRef.current?.id === dealId) closeDealDetails();
      return result;
    } finally {
      returnLocksRef.current.delete(lockKey);
    }
  }

  async function onDragEnd(result) {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const dealId = Number(draggableId);
    const newStage = destination.droppableId;
    const mutationResult = await updateInProgressDealStage(dealId, newStage);

    if (!mutationResult.ok) return;

    const deal = enrichDealWithClientResolution(mutationResult.data.deal, availableClients);

    setDeals((prev) =>
      prev.map((item) => (item.id === dealId ? deal : item))
    );

    if (selectedDealRef.current?.id === dealId) {
      selectedDealRef.current = deal;
      setSelectedDeal(deal);
    }
  }

  function handleCompleteDeal(dealId, paymentStatus) {
    const lockKey = String(dealId);
    if (completionLocksRef.current.has(lockKey)) {
      return completionPromisesRef.current.get(lockKey);
    }

    completionLocksRef.current.add(lockKey);
    setCompletionStates((prev) => ({
      ...prev,
      [lockKey]: { loading: true, error: '' },
    }));

    const request = (async () => {
      const result = await completeInProgressDeal(dealId, paymentStatus);
      const isConvergedReplay = result.error === 'DEAL_ALREADY_COMPLETED';

      if (!result.ok && !isConvergedReplay) {
        completionLocksRef.current.delete(lockKey);
        setCompletionStates((prev) => ({
          ...prev,
          [lockKey]: {
            loading: false,
            error: result.message || 'Completamento non riuscito.',
          },
        }));
        return result;
      }

      if (result.data?.accountingSyncError) toast({ title: 'Affare completato', description: result.data.accountingSyncError, variant: 'destructive' });
      setDeals((prev) => prev.filter((deal) => deal.id !== dealId));
      setCompletionStates((prev) => ({
        ...prev,
        [lockKey]: { loading: false, error: '' },
      }));

      if (selectedDealRef.current?.id === dealId) {
        closeDealDetails();
      }

      return isConvergedReplay
        ? {
            ...result,
            ok: true,
            replayed: true,
            error: null,
            message: '',
          }
        : result;
    })();
    completionPromisesRef.current.set(lockKey, request);
    request.finally(() => {
      completionPromisesRef.current.delete(lockKey);
      if (!completionLocksRef.current.has(lockKey)) return;
      completionLocksRef.current.delete(lockKey);
    });
    return request;
  }

  function clearFilters() {
    setFilters({
      searchTerm: '',
      urgencyFilter: 'all',
      assigneeFilter: 'all',
      stageFilter: 'all',
      clientFilter: clientFilterParam || 'all',
    });
    setActiveOverviewFilter(null);
  }

  return {
    dealStages,
    deals: visibleDeals,
    stats,
    isLoadingDeals,
    dealsModuleDisabled,
    dealsUnavailable,
    dealsError,
    dealsAuthRequired,
    dealsTenantDatabaseBlocked,
    selectedDeal,
    isDetailDialogOpen,
    isCreateDialogOpen,
    completionStates,
    staff: availableStaff,
    staffUnavailable,
    staffError,
    staffCatalogStatus: staffUnavailable ? 'unavailable' : availableStaff.length > 0 ? 'available' : 'empty',
    clients: availableClients,
    clientsUnavailable,
    clientsError,
    filters,
    assigneeOptions,
    hasActiveFilters,
    activeClientFilterLabel,
    activeOverviewFilter,
    isClientFiltered: effectiveClientFilter !== 'all',
    setSearchTerm: (value) => setFilters((prev) => ({ ...prev, searchTerm: value })),
    setUrgencyFilter: (value) => setFilters((prev) => ({ ...prev, urgencyFilter: value })),
    setAssigneeFilter: (value) => setFilters((prev) => ({ ...prev, assigneeFilter: value })),
    setStageFilter: (value) => setFilters((prev) => ({ ...prev, stageFilter: value })),
    setClientFilter: (value) => setFilters((prev) => ({ ...prev, clientFilter: clientFilterParam || value || 'all' })),
    setOverviewFilter,
    clearFilters,
    openDealDetails,
    closeDealDetails,
    openCreateDialog,
    closeCreateDialog,
    handleCreateDeal,
    handleSaveDeal,
    handleCancelDeal,
    handleReturnToProposal,
    handleCompleteDeal,
    onDragEnd,
  };
}

function buildLockedReturnResult() {
  return { ok: false, error: 'IN_PROGRESS_RETURN_PENDING', message: 'Ritorno a proposta già in corso.' };
}
